import { createContext, ReactNode, useEffect, useState, useContext } from 'react'
import {
  getAllEmissionTypes,
  getALlDeviceAntennas,
  getAllStationClasses,
  getAllRadarTunabilityTypes,
  getAllStationClassDescriptions,
  getAllRequestDeviceAntennas,
  getAllEmissionDesignatorSimpleTypes,
  getAllRequestStationClasses,
} from '../services/equipment.service'
import {
  IEmissionType,
  IStationClass,
  IDeviceAntenna,
  IRadarTunabilityTypes,
  IRequestEquipment,
  IRequestStationClass,
  IEmissionDesignatorSimpleType,
  IRequestDeviceAntenna,
  IRequestEquipmentContext,
} from '../types/equipment.type'
import { IDeviceTransmitter } from '../types/spectrum-manager.type'
import { getAllDeviceTransmitter } from '../services/spectrum-manager.service'
import { AuthContext } from './auth.context'

export const RequestEquipmentContext = createContext<IRequestEquipmentContext>({
  equipment: null,
  setEquipment: () => {},
  emissionTypes: [],
  deviceAntennas: [],
  stationClasses: [],
  radarTunabilityTypes: [],
  stationClassDescriptions: [],
  emissionDesignatorSimpleTypes: [],
  requestDeviceAntennas: [],
  requestStationClasses: [],
  deviceTransmitters: [],
  setRequestDeviceAntennas: () => {},
  setRequestStationClasses: () => {},
})

export const RequestEquipmentProvider = ({ children }: { children: ReactNode }) => {
  const [equipment, setEquipment] = useState<IRequestEquipment | null>(null)
  const [deviceAntennas, setDeviceAntennas] = useState<IDeviceAntenna[]>([])
  const [emissionTypes, setEmissionTypes] = useState<IEmissionType[]>([])
  const [stationClasses, setStationClasses] = useState<IStationClass[]>([])
  const [radarTunabilityTypes, setRadarTunabilityTypes] = useState<IRadarTunabilityTypes[]>([])
  const [stationClassDescriptions, setStationClassDescriptions] = useState<IStationClass[]>([])
  const [emissionDesignatorSimpleTypes, setEmissionDesignatorSimpleTypes] = useState<IEmissionDesignatorSimpleType[]>(
    [],
  )
  const [requestStationClasses, setRequestStationClasses] = useState<IRequestStationClass[]>([])
  const [requestDeviceAntennas, setRequestDeviceAntennas] = useState<IRequestDeviceAntenna[]>([])
  const [deviceTransmitters, setDeviceTransmitters] = useState<IDeviceTransmitter[]>([])

  const { user } = useContext(AuthContext)

  const getRequestStationClasses = async () => {
    if (equipment) {
      const data = await getAllRequestStationClasses(equipment.id)

      if (data.data && data.success) {
        setRequestStationClasses(data.data)
      }
    }
  }

  const getEmissionTypes = async () => {
    if (equipment?.device_id) {
      const data = await getAllEmissionTypes()

      if (data.data && data.success) {
        setEmissionTypes(data.data)
      }
    }
  }

  const getEmissionDesignatorSimpleTypes = async () => {
    if (equipment?.device_id) {
      const data = await getAllEmissionDesignatorSimpleTypes()

      if (data.data && data.success) {
        setEmissionDesignatorSimpleTypes(data.data)
      }
    }
  }

  const getRadarTunabilityTypes = async () => {
    const data = await getAllRadarTunabilityTypes()

    if (data.success && data.data) {
      setRadarTunabilityTypes(data.data)
    }
  }

  const getStationClasses = async () => {
    if (equipment?.device_id) {
      const data = await getAllStationClasses()

      if (data.data && data.success) {
        setStationClasses(data.data)
      }
    }
  }

  const getRequestDeviceAntennas = async () => {
    if (equipment) {
      const data = await getAllRequestDeviceAntennas(equipment.id)

      if (data.success && data.data) {
        setRequestDeviceAntennas(data.data)
      }
    }
  }

  const getDeviceAntennas = async () => {
    if (equipment?.device_id) {
      const data = await getALlDeviceAntennas(equipment?.device_id)

      if (data.success && data.data) {
        setDeviceAntennas(data.data)
      }
    }
  }

  const getStationClassDescriptions = async () => {
    if (equipment?.device_id) {
      const data = await getAllStationClassDescriptions()

      if (data.data && data.success) {
        setStationClassDescriptions(data.data)
      }
    }
  }

  const getDeviceTransmitters = async () => {
    if (equipment?.device_id) {
      const data = await getAllDeviceTransmitter(equipment?.device_id)

      if (data.success && data.data) {
        setDeviceTransmitters(data.data)
      }
    }
  }

  useEffect(() => {
    if (!user) return

    if (equipment) {
      getRadarTunabilityTypes()
      getRequestStationClasses()
      getRequestDeviceAntennas()

      if (equipment?.device_id) {
        getDeviceAntennas()
        getStationClasses()
        getEmissionTypes()
        getStationClassDescriptions()
        getEmissionDesignatorSimpleTypes()
        getDeviceTransmitters()
      }
    }
  }, [user, equipment])

  return (
    <RequestEquipmentContext.Provider
      value={{
        equipment,
        setEquipment,
        emissionTypes,
        deviceAntennas,
        stationClasses,
        radarTunabilityTypes,
        stationClassDescriptions,
        emissionDesignatorSimpleTypes,
        requestDeviceAntennas,
        requestStationClasses,
        deviceTransmitters,
        setRequestDeviceAntennas,
        setRequestStationClasses,
      }}
    >
      {children}
    </RequestEquipmentContext.Provider>
  )
}
